import React from 'react';
import {
  Modal,
  Box,
  Typography,
  Divider,
  Chip,
  LinearProgress,
  Stack,
  Tooltip,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SpellcheckIcon from '@mui/icons-material/Spellcheck';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90vw', sm: 560 },
  maxHeight: '85vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  borderRadius: 3,
  boxShadow: 24,
  p: 4,
};

const getMessageForScore = (score) => {
  if (score < 50) return "Needs improvement. Try shaping your resume to better match the job.";
  if (score < 70) return "Quite impressive. You're on the right track!";
  if (score < 90) return "Good chance. Resume aligns well with the job!";
  return "Excellent! High chance of selection based on this match.";
};

const scoreColor = (score) => {
  if (score < 50) return '#f44336';
  if (score < 70) return '#ff9800';
  if (score < 90) return '#2196f3';
  return '#4caf50';
};

const SimilarityResultModal = ({ open, onClose, data }) => {
  const {
    similarity_score = 0,
    semantic_score = 0,
    keyword_coverage = 0,
    matched_skills = [],
    missing_skills = [],
    spelling_errors = [],
  } = data;

  const color = scoreColor(similarity_score);

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        {/* Header */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          Resume Match Result
        </Typography>

        {/* Overall score */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
          <Typography variant="h3" sx={{ fontWeight: 'bold', color }}>
            {similarity_score}
          </Typography>
          <Typography variant="h6" color="text.secondary">/ 100</Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={Math.min(similarity_score, 100)}
          sx={{
            height: 8,
            borderRadius: 4,
            mb: 1,
            backgroundColor: '#e0e0e0',
            '& .MuiLinearProgress-bar': { backgroundColor: color, borderRadius: 4 },
          }}
        />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {getMessageForScore(similarity_score)}
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* Score breakdown */}
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Score Breakdown
        </Typography>
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">Semantic match</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{semantic_score}%</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(semantic_score, 100)}
            sx={{ height: 6, borderRadius: 3, backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': { backgroundColor: '#5c6bc0', borderRadius: 3 } }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">Keyword coverage</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{keyword_coverage}%</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(keyword_coverage, 100)}
            sx={{ height: 6, borderRadius: 3, backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': { backgroundColor: '#26a69a', borderRadius: 3 } }}
          />
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Missing skills */}
        {missing_skills.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <ErrorOutlineIcon sx={{ color: '#f44336', fontSize: 18 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#f44336' }}>
                Missing Skills ({missing_skills.length})
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
              {missing_skills.map((skill) => (
                <Chip
                  key={skill}
                  label={skill}
                  size="small"
                  sx={{
                    backgroundColor: '#fdecea',
                    color: '#c62828',
                    fontWeight: 500,
                    textTransform: 'capitalize',
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Matched skills */}
        {matched_skills.length > 0 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <CheckCircleOutlineIcon sx={{ color: '#4caf50', fontSize: 18 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                Matched Skills ({matched_skills.length})
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
              {matched_skills.map((skill) => (
                <Chip
                  key={skill}
                  label={skill}
                  size="small"
                  sx={{
                    backgroundColor: '#e8f5e9',
                    color: '#2e7d32',
                    fontWeight: 500,
                    textTransform: 'capitalize',
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {missing_skills.length === 0 && matched_skills.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No specific skills detected in the job description.
          </Typography>
        )}

        {/* Spelling errors */}
        {spelling_errors.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <SpellcheckIcon sx={{ color: '#f57c00', fontSize: 18 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                  Possible Spelling Errors ({spelling_errors.length})
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                {spelling_errors.map(({ word, suggestions }) => (
                  <Tooltip
                    key={word}
                    title={
                      suggestions.length > 0
                        ? `Did you mean: ${suggestions.join(', ')}?`
                        : 'No suggestions found'
                    }
                    arrow
                  >
                    <Chip
                      label={word}
                      size="small"
                      sx={{
                        backgroundColor: '#fff3e0',
                        color: '#e65100',
                        fontWeight: 500,
                        cursor: 'help',
                        textDecoration: 'underline dotted #e65100',
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Hover over a word to see suggestions. Names, locations, and tech terms are excluded.
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default SimilarityResultModal;